import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const AdminPanel = () => {
  const { user, userProfile, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('created_at');
  const [tabValue, setTabValue] = useState(0);
  const [approvalLoading, setApprovalLoading] = useState(null); // 승인 처리 중인 사용자 ID

  // 사용자 목록 로드
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      let query = supabase
        .from('profiles')
        .select('*');

      // 탭에 따라 필터링
      if (tabValue === 0) { // 승인 대기
        query = query.eq('is_approved', false);
      } else if (tabValue === 1) { // 승인됨
        query = query.eq('is_approved', true);
      }
      // tabValue === 2 (전체)는 필터링 없음

      // 정렬 적용
      switch (sortOption) {
        case 'created_at':
          query = query.order('created_at', { ascending: false });
          break;
        case 'name':
          query = query.order('name', { ascending: true, nullsLast: true });
          break;
        case 'email':
          query = query.order('email', { ascending: true });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setUsers(data || []);
    } catch (err) {
      setError('사용자 목록을 불러오는데 실패했습니다.');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  // 사용자 승인/거부 처리
  const handleApproval = async (userId, approve) => {
    try {
      setApprovalLoading(userId);
      setError(null);
      
      // 관리자 권한 확인
      if (!isAdmin) {
        throw new Error('관리자 권한이 필요합니다.');
      }
      
      // 현재 사용자가 실제로 관리자인지 DB에서 확인
      const { data: adminCheck, error: adminError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      
      if (adminError || adminCheck?.role !== 'admin') {
        throw new Error('관리자 권한이 확인되지 않았습니다.');
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          is_approved: approve,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select();

      if (error) {
        throw error;
      }

      // 목록 새로고침
      await loadUsers();
      
      setError(null);
    } catch (err) {
      const errorMessage = `사용자 ${approve ? '승인' : '승인 취소'} 처리 중 오류가 발생했습니다: ${err.message}`;
      setError(errorMessage);
      console.error('Error updating user approval:', err);
    } finally {
      setApprovalLoading(null);
    }
  };

  // 정렬 옵션 변경 처리
  const handleSortChange = (event) => {
    setSortOption(event.target.value);
  };

  // 탭 변경 처리
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    loadUsers();
  }, [sortOption, tabValue]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        사용자 관리
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                승인 대기
                {tabValue === 0 && (
                  <Chip 
                    label={users.length} 
                    size="small" 
                    color="warning" 
                    sx={{ minWidth: 24, height: 20 }}
                  />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                승인됨
                {tabValue === 1 && (
                  <Chip 
                    label={users.length} 
                    size="small" 
                    color="success" 
                    sx={{ minWidth: 24, height: 20 }}
                  />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                전체
                {tabValue === 2 && (
                  <Chip 
                    label={users.length} 
                    size="small" 
                    color="primary" 
                    sx={{ minWidth: 24, height: 20 }}
                  />
                )}
              </Box>
            } 
          />
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>정렬 기준</InputLabel>
          <Select
            value={sortOption}
            label="정렬 기준"
            onChange={handleSortChange}
          >
            <MenuItem value="created_at">가입일순 (최신순)</MenuItem>
            <MenuItem value="name">이름순</MenuItem>
            <MenuItem value="email">이메일순</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>이름</TableCell>
              <TableCell>이메일</TableCell>
              <TableCell>이메일 인증 상태</TableCell>
              <TableCell>가입일</TableCell>
              <TableCell>승인 상태</TableCell>
              <TableCell>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {tabValue === 0 ? '승인 대기 중인 사용자가 없습니다.' :
                     tabValue === 1 ? '승인된 사용자가 없습니다.' :
                     '등록된 사용자가 없습니다.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name || '(이름 없음)'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.email_verified ? (
                      <Chip label="인증됨" color="success" size="small" />
                    ) : (
                      <Chip label="미인증" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('ko-KR')}
                  </TableCell>
                  <TableCell>
                    {user.is_approved ? (
                      <Chip label="승인됨" color="success" size="small" />
                    ) : (
                      <Chip label="대기 중" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!user.is_approved ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleApproval(user.id, true)}
                          disabled={approvalLoading === user.id}
                          startIcon={approvalLoading === user.id ? <CircularProgress size={16} /> : null}
                        >
                          {approvalLoading === user.id ? '처리중...' : '승인'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleApproval(user.id, false)}
                          disabled={approvalLoading === user.id}
                          startIcon={approvalLoading === user.id ? <CircularProgress size={16} /> : null}
                        >
                          {approvalLoading === user.id ? '처리중...' : '승인 취소'}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminPanel; 